/** @jsxImportSource react */
import React, { useCallback, useEffect, useState } from 'react';
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext';
import { usePlatformStatus } from '@/contexts/PlatformStatusContext';
import {
  dismissHiringPauseBanner,
  isHiringPauseBannerDismissed,
} from '@/utils/hiringPauseBannerDismiss';
import { PauseIcon } from './PauseIcon';
import './HiringPauseBanner.css';

export interface HiringPauseBannerProps {
  audience: 'recruiter' | 'candidate';
}

export function HiringPauseBanner({ audience }: HiringPauseBannerProps) {
  const { t } = useThemeLanguage();
  const { shouldShowHiringPauseUI, hiringPauseMessage } = usePlatformStatus();
  const [dismissed, setDismissed] = useState(() => isHiringPauseBannerDismissed());

  useEffect(() => {
    setDismissed(isHiringPauseBannerDismissed());
  }, [shouldShowHiringPauseUI]);

  const handleDismiss = useCallback(() => {
    dismissHiringPauseBanner();
    setDismissed(true);
  }, []);

  if (!shouldShowHiringPauseUI() || dismissed) return null;

  const hint =
    audience === 'recruiter' ? t('hiringPause.recruiterHint') : t('hiringPause.candidateHint');

  return React.createElement(
    'div',
    {
      id: 'hiring-pause-banner',
      className: 'hiring-pause-banner hiring-pause-banner--sticky',
      role: 'status',
      'aria-live': 'polite',
    },
    React.createElement(
      'div',
      { className: 'hiring-pause-banner__content' },
      React.createElement(
        'span',
        { className: 'hiring-pause-banner__icon', 'aria-hidden': true },
        React.createElement(PauseIcon, { size: 20 }),
      ),
      React.createElement(
        'div',
        { className: 'hiring-pause-banner__text' },
        React.createElement('strong', { className: 'hiring-pause-banner__title' }, t('hiringPause.bannerTitle')),
        React.createElement('span', { className: 'hiring-pause-banner__message' }, hiringPauseMessage),
        React.createElement('span', { className: 'hiring-pause-banner__hint' }, hint),
      ),
      React.createElement(
        'button',
        {
          type: 'button',
          className: 'hiring-pause-banner__dismiss',
          onClick: handleDismiss,
          'aria-label': t('hiringPause.bannerDismiss'),
        },
        t('hiringPause.bannerDismiss'),
      ),
    ),
  );
}
